/* eslint-disable no-shadow */
import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    Organization,
    EmploymentPersonalInfoViewModel,
    CountryComponentWrapper,
    handleEmailOnKeyDown,
    HowDoesVerifyingWorkComponent,
    isFormFilled,
    RewardsRemainingComponent,
    setRef,
    shouldCollectAddressFields,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    VerificationStepsEnum,
    FormFooterComponent,
} from '@sheerid/jslib';
import { PhoneNumberComponent } from '../FormFields/PhoneNumber/PhoneNumberComponent';
import { CompanyComponent } from '../FormFields/CompanyComponent';
import MarketConsentWrapper from '../FormFields/MarketConsent/MarketConsentWrapper';
import { FirstNameComponent } from '../FormFields/FirstNameComponent';
import { LastNameComponent } from '../FormFields/LastNameComponent';
import { AddressComponent } from '../FormFields/AddressComponent';
import { CityComponent } from '../FormFields/CityComponent';
import { StateComponent } from '../FormFields/StateComponent';
import { PostalCodeComponent } from '../FormFields/PostalCodeComponent';
import { EmailComponent } from '../FormFields/EmailComponent';

interface StepEmploymentPersonalInfoComponentProps {
    verificationService: VerificationService;
}

const StepEmploymentPersonalInfo = ({ verificationService }: StepEmploymentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as EmploymentPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;

    const updateEmploymentViewModel = (key: keyof EmploymentPersonalInfoViewModel, value: any) => {
        const nextState: EmploymentPersonalInfoViewModel = produce(viewModel, (draft: EmploymentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
            if (key === 'phoneNumber') {
                draft.metadata.phoneNumber = value;
            }
        });
        verificationService.updateViewModel(nextState);
    };

    return (
        <div id="sid-step-employment-personal-info" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-header__title sid-l-horz-center">
                    <FormattedHTMLMessage
                        id="step.personalInfo.title"
                        defaultMessage="Unlock this Employment-Only Offer"
                        tagName="h1"
                    />
                </div>

                <div className="sid-header__subtitle sid-l-horz-center">
                    <FormattedHTMLMessage
                        id="step.personalInfo.subtitle"
                        defaultMessage="Verify you're a current employee at an eligible company."
                    />
                    &nbsp;
                    <HowDoesVerifyingWorkComponent verificationService={verificationService} />
                    <RewardsRemainingComponent verificationService={verificationService} />
                </div>
            </div>

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
                viewModelDraftDecorator={(draft, countryChoice) => {
                    const viewModel = draft as EmploymentPersonalInfoViewModel;

                    viewModel.shouldCollectAddress = shouldCollectAddressFields(countryChoice, verificationService.programTheme.config.countries);
                    if (!viewModel.shouldCollectAddress) {
                        viewModel.address1 = '';
                        viewModel.city = '';
                        viewModel.state = '';
                        viewModel.postalCode = '';
                        viewModel.fieldsToSkipValidation = ['address1', 'city', 'state', 'postalCode'];
                    } else {
                        viewModel.fieldsToSkipValidation = undefined;
                    }
                }}
            />

            <CompanyComponent
                value={(verificationService.viewModel as EmploymentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-employee-explanation sid-l-space-top-sm">
                <FormattedHTMLMessage id="step.personalInfo.orgExplanation" defaultMessage="If you do not see your company on the list, please visit one of our retail locations and a UScellular associate will be able to help you with your request." />
            </div>

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue: boolean) => {
                    updateEmploymentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <FirstNameComponent
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                onChange={
                    (newValue: string) => {
                        updateEmploymentViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastNameComponent
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                onChange={
                    (newValue: string) => {
                        updateEmploymentViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            {
                shouldCollectAddressFields(viewModel.countryChoice, verificationService.programTheme.config.countries)
                    ? (
                        <AddressComponent
                            isErrored={!!fieldValidationErrors.address1}
                            errorId={fieldValidationErrors.address1}
                            onChange={(newValue: string) => {
                                updateEmploymentViewModel('address1', newValue);
                                updateFieldValidationErrorsByFieldId('address1', newValue, verificationService);
                            }}
                            value={viewModel.address1}
                        />
                    )
                    : null
            }

            {
                shouldCollectAddressFields(viewModel.countryChoice, verificationService.programTheme.config.countries)
                    ? (
                        <div className="sid-thirds">
                            <CityComponent
                                isErrored={!!fieldValidationErrors.city}
                                errorId={fieldValidationErrors.city}
                                onChange={(newValue: string) => {
                                    updateEmploymentViewModel('city', newValue);
                                    updateFieldValidationErrorsByFieldId('city', newValue, verificationService);
                                }}
                                value={viewModel.city}
                            />

                            <StateComponent
                                isErrored={!!fieldValidationErrors.state}
                                errorId={fieldValidationErrors.state}
                                onChange={(newValue: string) => {
                                    updateEmploymentViewModel('state', newValue);
                                    updateFieldValidationErrorsByFieldId('state', newValue, verificationService);
                                }}
                                value={viewModel.state}
                            />

                            <PostalCodeComponent
                                isErrored={!!fieldValidationErrors.postalCode}
                                onChange={(newValue: string) => {
                                    updateEmploymentViewModel('postalCode', newValue);
                                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                                }}
                                value={viewModel.postalCode}
                            />
                        </div>
                    )
                    : null
            }

            <PhoneNumberComponent
                isRequired
                value={viewModel.phoneNumber}
                isErrored={!!fieldValidationErrors.phoneNumber}
                onChange={
                    (newValue: string) => {
                        updateEmploymentViewModel('phoneNumber', newValue);
                        updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                    }
                }
            />

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                )}
                onChange={
                    (newValue: string) => {
                        updateEmploymentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectEmployeePersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            <FormattedHTMLMessage id="verifyMyEmploymentStatus" defaultMessage="Verify My Employment Status" />
                        </span>
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService} />
        </div>
    );
};

export const StepEmploymentPersonalInfoComponent = injectIntl(StepEmploymentPersonalInfo);
